/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { cx } from '@emotion/css';
import React, { useState, useEffect, useLayoutEffect, useRef, forwardRef } from 'react';
import { Link } from '@swp/link-provider';
import { SiteHeaderStyled, SiteHeaderWrapStyled, SiteHeaderContentStyled, SiteHeaderBtn, LogoStyled, NavStyled, SocialStyled, HamburgerIcon, HamburgerContent, appearFromLeft, appearFromTop, appearFromRight, hamburgerSlideIn } from './styles.js';
import './index.css';
var LOGO_SIZE = {
  S: 's',
  M: 'm',
  L: 'l',
  XL: 'xl'
};

var getTo = function getTo(_ref) {
  var page = _ref.page,
      section = _ref.section;
  return "" + page + (section ? "#" + section : '');
};

var getHref = function getHref(_ref2) {
  var use = _ref2.use,
      url = _ref2.url,
      document = _ref2.document;
  return use === 'url' ? url : use === 'file' ? document === null || document === void 0 ? void 0 : document.value : undefined;
};

var getTarget = function getTarget(_ref3) {
  var use = _ref3.use,
      blank = _ref3.blank;
  return use === 'file' || blank !== false ? '_blank' : undefined;
};

var getStyle = function getStyle(_ref4) {
  var color = _ref4.color,
      colorHover = _ref4.colorHover,
      backgroundColor = _ref4.backgroundColor;
  return {
    '--color': color,
    '--color-hover': colorHover,
    '--background-color': backgroundColor
  };
}; // keep as separate component for effect cleanup


function Hamburger(_ref5) {
  var isOpen = _ref5.isOpen,
      setIsOpen = _ref5.setIsOpen,
      color = _ref5.color,
      children = _ref5.children,
      edit = _ref5.edit;
  useEffect(function () {
    if (isOpen) {
      document.body.classList.add('siteHeaderOpen');
    }

    return function () {
      return document.body.classList.remove('siteHeaderOpen');
    };
  }, [isOpen]);
  return jsx(HamburgerIcon, {
    style: getStyle({
      color: color
    }),
    className: cx(isOpen && 'open'),
    onClick: function onClick(event) {
      if (process.env.ENV_RAW || !edit) {
        setIsOpen(function (i) {
          return !i;
        });
      }
    }
  }, jsx("span", null), jsx("span", null), jsx("span", null), jsx("span", null));
}

var Logo = /*#__PURE__*/forwardRef(function Logo(_ref6, ref) {
  var logo = _ref6.logo,
      logoMobile = _ref6.logoMobile,
      title = _ref6.title;
  if (!logo && !title) return null;
  return jsx(LogoStyled, {
    style: getStyle(title)
  }, logo !== null && logo !== void 0 && logo.src ? jsx(Link, {
    to: "/"
  }, jsx("picture", {
    className: LOGO_SIZE[logo.size || 'M']
  }, logoMobile !== null && logoMobile !== void 0 && logoMobile.src ? jsx("source", {
    media: "(max-width: 768px)",
    srcSet: logoMobile.src
  }) : null, jsx("img", {
    src: logo.src,
    alt: "Logo",
    className: LOGO_SIZE[logo.size || 'M'],
    ref: ref
  }))) : null, title !== null && title !== void 0 && title.content ? jsx(Link, {
    to: "/",
    className: title.className
  }, title.content) : null);
});
var Items = /*#__PURE__*/forwardRef(function Items(_ref7, ref) {
  var _ref7$items = _ref7.items,
      items = _ref7$items === void 0 ? [] : _ref7$items,
      item = _ref7.item,
      onClose = _ref7.onClose;
  var cls = item.className;
  return jsx(NavStyled, {
    ref: ref
  }, items.map(function (element) {
    var id = element.id,
        name = element.name;
    var style = getStyle(item);

    if (!element.use || element.use === 'page') {
      var to = getTo(element);
      return jsx(Link, {
        key: id,
        className: cls,
        to: to,
        style: style,
        onClick: onClose
      }, name);
    }

    var href = getHref(element);
    var target = getTarget(element);
    return jsx("a", {
      className: cls,
      key: id,
      style: style,
      href: href,
      target: target,
      onClick: onClose
    }, name);
  }));
});

function Social(_ref8) {
  var _ref8$social = _ref8.social,
      social = _ref8$social === void 0 ? {} : _ref8$social,
      item = _ref8.item;
  var facebook = social.facebook,
      instagram = social.instagram,
      twitter = social.twitter;
  var style = getStyle(item);
  if (!facebook && !instagram && !twitter) return null;
  return jsx(SocialStyled, {
    style: style
  }, facebook ? jsx("a", {
    className: item.className,
    href: "https://facebook.com/" + facebook,
    rel: "noreferrer noopener",
    target: "_blank",
    title: "/" + facebook
  }, jsx("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 48 48"
  }, jsx("path", {
    fill: "currentColor",
    d: "M48.15 24.15A24.15 24.15 0 1020.23 48V31.13H14.1v-6.98h6.13v-5.32c0-6.05 3.6-9.4 9.12-9.4 2.64 0 5.4.47 5.4.47v5.95h-3.04c-3 0-3.94 1.86-3.94 3.77v4.53h6.7l-1.07 6.98h-5.63V48a24.15 24.15 0 0020.38-23.85z"
  }))) : null, instagram ? jsx("a", {
    className: item.className,
    href: "https://instagram.com/" + instagram,
    rel: "noreferrer noopener",
    target: "_blank",
    title: "@" + instagram
  }, jsx("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 48 48"
  }, jsx("g", {
    fill: "currentColor"
  }, jsx("path", {
    d: "M24 4.37c6.4 0 7.17.03 9.7.14 2.34.11 3.6.5 4.45.83 1.12.44 1.92.96 2.76 1.8a7.44 7.44 0 011.8 2.76c.33.84.72 2.11.83 4.45.11 2.53.14 3.3.14 9.7s-.03 7.17-.14 9.7c-.11 2.34-.5 3.6-.83 4.45a7.44 7.44 0 01-1.8 2.76 7.44 7.44 0 01-2.76 1.8c-.84.33-2.11.72-4.45.83-2.53.11-3.3.14-9.7.14s-7.17-.03-9.7-.14c-2.34-.11-3.6-.5-4.45-.83a7.44 7.44 0 01-2.76-1.8 7.44 7.44 0 01-1.8-2.76c-.33-.84-.72-2.11-.83-4.45-.11-2.53-.14-3.3-.14-9.7s.03-7.17.14-9.7c.11-2.34.5-3.6.83-4.45a7.44 7.44 0 011.8-2.76 7.44 7.44 0 012.76-1.8c.84-.33 2.11-.72 4.45-.83 2.53-.11 3.3-.14 9.7-.14m0-4.32c-6.52 0-7.34.03-9.9.14-2.55.12-4.3.53-5.82 1.12a11.76 11.76 0 00-4.25 2.77 11.76 11.76 0 00-2.77 4.25c-.6 1.53-1 3.27-1.12 5.82-.11 2.56-.14 3.38-.14 9.9 0 6.52.03 7.34.14 9.9.12 2.55.53 4.3 1.12 5.82a11.76 11.76 0 002.77 4.25 11.76 11.76 0 004.25 2.77c1.53.6 3.27 1 5.82 1.12 2.56.11 3.38.14 9.9.14s7.34-.03 9.9-.14c2.55-.12 4.3-.53 5.82-1.12a11.76 11.76 0 004.25-2.77 11.76 11.76 0 002.77-4.25c.6-1.53 1-3.27 1.12-5.82.11-2.56.14-3.38.14-9.9s-.03-7.34-.14-9.9c-.12-2.55-.53-4.3-1.12-5.82a11.76 11.76 0 00-2.77-4.25 11.76 11.76 0 00-4.25-2.77C38.2.71 36.45.31 33.9.19 31.34.08 30.52.05 24 .05z"
  }), jsx("path", {
    d: "M24 11.73a12.32 12.32 0 100 24.64 12.32 12.32 0 000-24.64zm0 20.32a8 8 0 110-16 8 8 0 010 16z"
  }), jsx("circle", {
    cx: "36.81",
    cy: "11.24",
    r: "2.88"
  })))) : null, twitter ? jsx("a", {
    className: item.className,
    href: "https://twitter.com/" + twitter,
    rel: "noreferrer noopener",
    target: "_blank",
    title: "@" + twitter
  }, jsx("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 48 48"
  }, jsx("path", {
    fill: "currentColor",
    d: "M43.22 14.4c.03.41.03.83.03 1.25 0 12.76-9.71 27.47-27.47 27.47-5.25 0-10.39-1.5-14.8-4.33a19.39 19.39 0 0014.3-4 9.67 9.67 0 01-9.03-6.71c1.44.28 2.94.22 4.36-.17a9.66 9.66 0 01-7.75-9.46v-.13a9.6 9.6 0 004.38 1.21A9.67 9.67 0 014.25 6.64a27.4 27.4 0 0019.9 10.09 9.66 9.66 0 0116.46-8.8 19.38 19.38 0 006.13-2.35 9.7 9.7 0 01-4.24 5.34c1.91-.23 2.76-.74 4.52-1.52-1.3 1.94-1.9 3.63-3.8 5z"
  }))) : null);
}

function Button(_ref9) {
  var button = _ref9.button,
      item = _ref9.item,
      onClose = _ref9.onClose;
  var className = button.className,
      _button$use = button.use,
      use = _button$use === void 0 ? 'page' : _button$use,
      content = button.content;
  if (!content) return null;
  var style = getStyle(button);
  return jsx(SiteHeaderBtn, {
    className: className
  }, use === 'page' ? jsx(Link, {
    className: "btn",
    style: style,
    to: getTo(button),
    onClick: onClose
  }, content) : jsx("a", {
    className: "btn",
    style: style,
    href: getHref(button),
    target: getTarget(button),
    onClick: onClose
  }, content));
}

var getWindowScrollY = function getWindowScrollY() {
  var _window;

  return ((_window = window) === null || _window === void 0 ? void 0 : _window.scrollY) || 0;
};

var isWindowScrolled = function isWindowScrolled() {
  return getWindowScrollY() > 0;
}; // keep the "logo" keys


export function SiteHeader(_ref10) {
  var backgroundColor = _ref10.backgroundColor,
      className = _ref10.className,
      _ref10$uiEvents = _ref10.uiEvents,
      uiEvents = _ref10$uiEvents === void 0 ? null : _ref10$uiEvents,
      _ref10$layout = _ref10.layout,
      layout = _ref10$layout === void 0 ? '1' : _ref10$layout,
      _ref10$layoutMobile = _ref10.layoutMobile,
      layoutMobile = _ref10$layoutMobile === void 0 ? '1' : _ref10$layoutMobile,
      logo = _ref10.logo,
      logoMobile = _ref10.logoMobile,
      title = _ref10.title,
      item = _ref10.item,
      items = _ref10.items,
      social = _ref10.social,
      children = _ref10.children,
      button = _ref10.button,
      edit = _ref10.edit,
      transparent = _ref10.transparent;
  var root = useRef();
  var lastOverflowAt = useRef();

  var _useState = useState(),
      overflowRef = _useState[0],
      setOverflowRef = _useState[1];

  var _useState2 = useState(),
      logoRef = _useState2[0],
      setLogoRef = _useState2[1];

  var _useState3 = useState(false),
      isOpen = _useState3[0],
      setIsOpen = _useState3[1];

  var _useState4 = useState(isScrolled),
      isScrolled = _useState4[0],
      setIsScrolled = _useState4[1];

  var _useState5 = useState(false),
      isOverflowing = _useState5[0],
      setIsOverflowing = _useState5[1];

  var documentFontsReady = useRef(false);
  useEffect(function () {
    if (!transparent) return;

    var listener = function listener() {
      return setIsScrolled(isWindowScrolled());
    };

    window.addEventListener('scroll', listener, {
      passive: true
    });
    return function () {
      return window.removeEventListener('scroll', listener, {
        passive: true
      });
    };
  }, [transparent]);
  useLayoutEffect(function () {
    var listener = function listener() {
      if (overflowRef) {
        var _isOverflowing = overflowRef.scrollWidth > overflowRef.clientWidth;

        setIsOverflowing(_isOverflowing);

        if (_isOverflowing) {
          lastOverflowAt.current = window.innerWidth;
          setOverflowRef(null);
        }
      } else if (window.innerWidth > lastOverflowAt.current) {
        setTimeout(function () {
          return setIsOverflowing(false);
        }, 0);
      }
    };

    listener(); // damn

    if (overflowRef && (!(logo !== null && logo !== void 0 && logo.src) || logoRef) && !documentFontsReady.current) {
      var _document$fonts, _document$fonts$ready;

      documentFontsReady.current = true;
      (_document$fonts = document.fonts) === null || _document$fonts === void 0 ? void 0 : (_document$fonts$ready = _document$fonts.ready) === null || _document$fonts$ready === void 0 ? void 0 : _document$fonts$ready.then(listener);
    }

    window.addEventListener('resize', listener, {
      passive: true
    });
    logoRef === null || logoRef === void 0 ? void 0 : logoRef.addEventListener('load', listener);
    return function () {
      window.removeEventListener('resize', listener, {
        passive: true
      });
      logoRef === null || logoRef === void 0 ? void 0 : logoRef.removeEventListener('load', listener);
    };
  }, [logo === null || logo === void 0 ? void 0 : logo.src, logoRef, overflowRef]);
  useEffect(function () {
    var listener = function listener(event) {
      if (!root.current) return;
      if (root.current.contains(event.target)) return;
      setIsOpen(false);
    };

    document.addEventListener('click', listener);
    return function () {
      return document.removeEventListener('click', listener);
    };
  }, [setIsOpen]);
  return jsx(SiteHeaderStyled, Object.assign({
    ref: root,
    style: {
      backgroundColor: backgroundColor
    },
    className: cx(className, isScrolled && 'js-is-scrolled', isOpen && 'open'),
    layout: layout,
    onClick: function onClick(event) {
      if (process.env.ENV_RAW || !edit) {
        event.stopPropagation();
      }
    }
  }, process.env.ENV_RAW ? null : uiEvents), isOverflowing ? jsx(React.Fragment, null, jsx(SiteHeaderWrapStyled, {
    className: "simple"
  }, jsx(Logo, {
    key: "logo",
    logo: logo,
    logoMobile: logoMobile,
    title: title
  }), jsx(Hamburger, {
    isOpen: isOpen,
    setIsOpen: setIsOpen,
    color: item === null || item === void 0 ? void 0 : item.color,
    edit: edit
  })), isOpen ? jsx(SiteHeaderWrapStyled, {
    css: hamburgerSlideIn,
    layout: layoutMobile
  }, jsx(HamburgerContent, {
    className: cx(isOpen && 'open')
  }, jsx(Items, {
    items: items,
    item: item,
    onClose: function onClose() {
      return setIsOpen(false);
    }
  }), jsx(SiteHeaderContentStyled, null, jsx(Social, {
    social: social,
    item: item
  }), jsx(Button, {
    button: button,
    item: item,
    onClose: function onClose() {
      return setIsOpen(false);
    }
  })))) : null) : jsx(React.Fragment, null, jsx(SiteHeaderWrapStyled, null, jsx(Logo, {
    key: "logo",
    logo: logo,
    logoMobile: logoMobile,
    title: title,
    ref: setLogoRef
  }), jsx(Items, {
    items: items,
    item: item,
    ref: setOverflowRef
  }), jsx(SiteHeaderContentStyled, null, jsx(Social, {
    social: social,
    item: item
  }), jsx(Button, {
    button: button,
    item: item
  })))), children);
}